import MatchingTransactionsDialog from '@/components/rules/dialogs/MatchingTransactionsDialog';
import useImperativeDialogRef from '@/components/shared/dialogs/useImperativeDialogRef';
import { Button } from '@/components/ui/button';
import { useMatchingTransactionsCount } from '@/hooks/use-matching-transactions-count';
import { AIRuleResponse } from '@polybank/shared/types/ai-contracts';
import { Eye, Loader2 } from 'lucide-react';
import { motion } from 'motion/react';
import { memo, useCallback } from 'react';

interface MatchingTransactionsCountCardProps {
  rule?: AIRuleResponse;
  isDebouncing?: boolean;
}

const MatchingTransactionsCountCard = memo(
  ({ rule, isDebouncing }: MatchingTransactionsCountCardProps) => {
    const { count: matchingCount, isLoading: isLoadingCount } =
      useMatchingTransactionsCount(rule);
    const isLoading = isLoadingCount || !!isDebouncing;

    const {
      ref: matchingTransactionsModalRef,
      open: openMatchingTransactionsModal,
    } = useImperativeDialogRef();
    const handleViewTransactions = useCallback(() => {
      openMatchingTransactionsModal();
    }, [openMatchingTransactionsModal]);

    return (
      <>
        {/* Matching Transactions Count */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
          className='p-4 bg-purple-50/50 dark:bg-purple-950/20 rounded-xl border border-purple-200 dark:border-purple-800'
        >
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              {/* Circular purple badge with count */}
              <div className='w-11 h-11 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center'>
                {isLoading ? (
                  <Loader2 className='w-5 h-5 animate-spin text-purple-600 dark:text-purple-400' />
                ) : (
                  <motion.span
                    key={matchingCount}
                    initial={{ scale: 1.2, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className='text-lg font-bold text-purple-600 dark:text-purple-400'
                  >
                    {matchingCount}
                  </motion.span>
                )}
              </div>
              <div>
                <p className='text-sm font-medium text-foreground'>
                  Transactions matching this rule
                </p>
                <p className='text-xs text-muted-foreground'>
                  Based on your current settings
                </p>
              </div>
            </div>
            {!isLoading && matchingCount > 0 && (
              <Button
                variant='outline'
                onClick={handleViewTransactions}
                className='gap-2 h-9 border-purple-200 dark:border-purple-800 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-700 dark:hover:text-purple-300'
              >
                <Eye className='w-4 h-4' />
                View All
              </Button>
            )}
          </div>
        </motion.div>

        <MatchingTransactionsDialog
          rawRule={rule!}
          ref={matchingTransactionsModalRef}
        />
      </>
    );
  }
);
MatchingTransactionsCountCard.displayName = 'MatchingTransactionsCountCard';
export default MatchingTransactionsCountCard;
